// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  getPatientStatusCategory,
  getStatusCategoryTitle,
  isTestPatient,
  PATIENT_SYSTEM_STATUS,
  POLARIS_SYSTEM_STATUS_TAG_SYSTEM,
} from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

export interface InactiveTriageEntry {
  id: string;
  patientName: string;
  patientSubtitle: string;
  lastActive: string | undefined;
  reason: string;
  lastStage: string;
  isTestPatient: boolean;
}

function mapPatient(patient: Patient): InactiveTriageEntry {
  const systemStatus = patient.meta?.tag?.find((tag) => tag.system === POLARIS_SYSTEM_STATUS_TAG_SYSTEM);
  const subtitle = [
    patient.birthDate ? `${new Date().getFullYear() - new Date(patient.birthDate).getFullYear()}` : undefined,
    patient.gender,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    id: patient.id ?? 'unknown',
    patientName: patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient',
    patientSubtitle: subtitle,
    lastActive: patient.meta?.lastUpdated,
    reason: systemStatus?.code === PATIENT_SYSTEM_STATUS.inactive ? 'Inactive' : 'Inactive patient',
    lastStage: getStatusCategoryTitle(getPatientStatusCategory(patient)),
    isTestPatient: isTestPatient(patient),
  };
}

export function useInactiveTriageQueue({
  page,
  pageSize,
  search,
  hideTestPatients,
}: {
  page: number;
  pageSize: number;
  search: string;
  hideTestPatients: boolean;
}): { entries: InactiveTriageEntry[]; total: number; loading: boolean } {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<InactiveTriageEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const patients = await medplum.searchResources('Patient', {
        _tag: `${POLARIS_SYSTEM_STATUS_TAG_SYSTEM}|${PATIENT_SYSTEM_STATUS.inactive}`,
        _count: '500',
        _sort: '-_lastUpdated',
      });
      const query = search.trim().toLowerCase();
      const filtered = patients
        .map(mapPatient)
        .filter((entry) => !query || entry.patientName.toLowerCase().includes(query))
        .filter((entry) => !hideTestPatients || !entry.isTestPatient);
      const offset = (page - 1) * pageSize;
      setTotal(filtered.length);
      setEntries(filtered.slice(offset, offset + pageSize));
    } finally {
      setLoading(false);
    }
  }, [hideTestPatients, medplum, page, pageSize, search]);
  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);
  return { entries, total, loading };
}
